import apiClient from './apiClient';
import {
  cacheData,
  cacheMultiple,
  getCachedData,
  getAllCachedData,
  setMetadata,
  STORES
} from '../utils/offlineDB';

// GET ALL - IMMER vom Server laden wenn online (für aktuelle _count Werte)
// Cache nur als Offline-Fallback nutzen
export const getAllKunden = async (searchParams = {}) => {
  try {
    // Wenn offline, nutze Cache
    if (!navigator.onLine) {
      console.log('📦 Kunden aus Cache geladen (offline)');
      const cached = await getAllCachedData(STORES.CACHE_KUNDEN);

      // Wenn Suchparameter, filtere lokal
      if (searchParams.suche) {
        const searchLower = searchParams.suche.toLowerCase();
        return cached.filter(kunde =>
          kunde.name?.toLowerCase().includes(searchLower) ||
          kunde.email?.toLowerCase().includes(searchLower) ||
          kunde.telefon?.includes(searchParams.suche) ||
          kunde.adresse?.toLowerCase().includes(searchLower)
        );
      }

      return cached;
    }

    // Online: IMMER vom Server holen für aktuelle Daten
    const response = await apiClient.get('/kunden', { params: searchParams });
    const kunden = response.data;

    // Cache für Offline aktualisieren (nur wenn keine Suchparameter)
    if (Object.keys(searchParams).length === 0) {
      await cacheMultiple(STORES.CACHE_KUNDEN, kunden);
      await setMetadata(`${STORES.CACHE_KUNDEN}-last-sync`, Date.now());
    }

    return kunden;

  } catch (error) {
    // Bei Fehler: Cache als Fallback nutzen
    console.log('📦 Kunden aus Cache (Fehler)', error.message);
    const cached = await getAllCachedData(STORES.CACHE_KUNDEN);

    if (cached.length > 0) {
      // Wenn Suchparameter, filtere lokal
      if (searchParams.suche) {
        const searchLower = searchParams.suche.toLowerCase();
        return cached.filter(kunde =>
          kunde.name?.toLowerCase().includes(searchLower) ||
          kunde.email?.toLowerCase().includes(searchLower) ||
          kunde.telefon?.includes(searchParams.suche) ||
          kunde.adresse?.toLowerCase().includes(searchLower)
        );
      }
      return cached;
    }

    throw error;
  }
};

// GET BY ID mit Cache-Support
export const getKundeById = async (id) => {
  try {
    if (navigator.onLine) {
      const response = await apiClient.get(`/kunden/${id}`);
      const kunde = response.data;

      // Cache einzelnen Kunden
      await cacheData(STORES.CACHE_KUNDEN, kunde);

      return kunde;
    }

    // Offline: Aus Cache
    console.log('📦 Kunde aus Cache geladen (offline)');
    return await getCachedData(STORES.CACHE_KUNDEN, id);

  } catch (error) {
    console.log('📦 Kunde aus Cache (Fehler)', error.message);
    const cached = await getCachedData(STORES.CACHE_KUNDEN, id);
    if (cached) {
      return cached;
    }
    throw error;
  }
};

export const createKunde = async (kundeData) => {
  const response = await apiClient.post('/kunden', kundeData);

  // Cache aktualisieren
  if (response.data?.kunde) {
    await cacheData(STORES.CACHE_KUNDEN, response.data.kunde);
  }

  return response.data;
};

export const updateKunde = async (id, kundeData) => {
  const response = await apiClient.put(`/kunden/${id}`, kundeData);

  // Cache aktualisieren
  if (response.data?.kunde) {
    await cacheData(STORES.CACHE_KUNDEN, response.data.kunde);
  }

  return response.data;
};

export const deleteKunde = async (id) => {
  const response = await apiClient.delete(`/kunden/${id}`);

  // Aus Cache entfernen (durch erneutes Cachen aller Kunden)
  // Da wir keine deleteFromCache Funktion haben, wird der Cache
  // beim nächsten getAllKunden refresh aktualisiert

  return response.data;
};

// Mehrere Kunden auf einmal löschen
export const bulkDeleteKunden = async (ids) => {
  const response = await apiClient.post('/kunden/bulk-delete', { ids });
  return response.data;
};

// Sekundärnummern (Multi-Tel) — Disponent-kuratiert, werden beim Caller-ID-
// Lookup zusätzlich zur Hauptnummer (Kunde.telefon) konsultiert.
export const createKundeTelefon = async (kundeId, { nummer, label }) => {
  const response = await apiClient.post(`/kunden/${kundeId}/telefon`, { nummer, label });
  return response.data;
};

export const updateKundeTelefon = async (kundeId, telefonId, { nummer, label }) => {
  const response = await apiClient.put(`/kunden/${kundeId}/telefon/${telefonId}`, { nummer, label });
  return response.data;
};

export const deleteKundeTelefon = async (kundeId, telefonId) => {
  const response = await apiClient.delete(`/kunden/${kundeId}/telefon/${telefonId}`);
  return response.data;
};

// Dateien eines Kunden als ZIP herunterladen
// type: 'fotos' | 'dokumente' (optional, wenn nicht angegeben werden beide heruntergeladen)
export const downloadAllFiles = async (kundeId, type) => {
  const params = type ? { type } : {};
  const response = await apiClient.get(`/kunden/${kundeId}/download-all`, {
    params,
    responseType: 'blob'
  });
  return response.data;
};

// Kunden als CSV exportieren
export const exportCsv = async () => {
  const response = await apiClient.get('/kunden/export/csv', {
    responseType: 'blob'
  });
  return response.data;
};

// Kunden aus CSV importieren
export const importCsv = async (file) => {
  const formData = new FormData();
  formData.append('csv', file);

  const response = await apiClient.post('/kunden/import/csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Cache manuell refreshen
export const refreshCache = async () => {
  try {
    const response = await apiClient.get('/kunden');
    const kunden = response.data;
    await cacheMultiple(STORES.CACHE_KUNDEN, kunden);
    await setMetadata(`${STORES.CACHE_KUNDEN}-last-sync`, Date.now());
    console.log('✅ Kunden-Cache refreshed:', kunden.length, 'Kunden');
    return kunden;
  } catch (error) {
    console.error('Cache refresh failed:', error);
    throw error;
  }
};
